<script setup lang="ts">
/**
 * Module: web/src/layouts/AppShell.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.24.0
 *
 * v1.24.0 (Engine Performance, decisions #180 + #185 / contract
 * performance-api v1.2.0 SS7): Administration-group nav item 'Engine
 * Performance' (robot icon -- it IS the AI-engine surface), gated
 * engine_performance_view exactly like its route.
 *
 * v1.23.0 (Team Performance, decision #180 / contract performance-api
 * v1.0.0): Staff-group nav item 'Team Performance' (bolt icon), gated
 * performance_view exactly like its route.
 *
 * v1.22.0 (the /review split executes -- contract entry-log-api v1.0.0 SS5,
 * AE slice 4, decisions #176 + #177): the INTERIM "Call Review" seat is
 * REMOVED; "Screened Out" (recordings_listen) + "Entry Log" (the NEW
 * entry_log_view cap) join the Missed Opportunities group. MO group order:
 * Entry Queue - My Entries - Screened Out - Entry Log - Reports. The Calls
 * group is Call History alone until the next tenant.
 *
 * v1.21.0 (domain-grouped nav -- decision #176, Robert 2026-07-19; AE slice
 * 3, labels/groups ONLY -- routes, caps, API paths all unchanged): the
 * generic Workspace/Management containers become BUSINESS-DOMAIN groups --
 * Missed Opportunities (Entry Queue [was Assisted-Entry Review], My
 * Entries, Reports [was Reporting]), Calls (Call History, Call Review
 * [interim seat/label for /review until slice 4 splits it into Screened
 * Out + Entry Log]), Staff (Review Incentives, name kept), Administration
 * (unchanged), Help (bottom, unheaded). The domain word "Missed
 * Opportunities" returns to the UI and the three colliding "Review"s are
 * dead. STRUCTURAL: both nav surfaces now render from ONE group-driven
 * loop (NAV_GROUPS + visibleGroups) instead of three copy-pasted blocks
 * each -- the "feature added to some loops but not others" class (durable
 * #20, the v1.12.1 RI-chip bug) is dead by construction.
 *
 * v1.20.1 (mobile overlap fix, same feature): the v1.20.0 max-w-[58vw] cap
 * did NOT bind once flexbox shrank the min-w-0 wrapper -- at <=~400px the img
 * (still 58vw wide) spilled ~36px under the theme button (measured: logo
 * right edge 278px vs sun button x 242px at 375w). Replaced with max-w-full +
 * object-left: the img now obeys whatever width the squeeze actually leaves
 * its wrapper and scales down proportionally, left-anchored, on every
 * viewport incl. 320px -- flexbox does the math instead of a hand-tuned vw.
 *
 * v1.20.0 (brand logo -- Robert, 2026-07-19): the header identity is now the
 * horizontal "Commercial Towing" one-line badge (settings.brandLogo) in place
 * of the stacked wordmark/subline text, in both the top bar and the mobile
 * drawer header. Sized to the header height (h-7 mobile / h-8 desktop);
 * brandWordmark stays the alt text. Non-interactive, matching
 * the prior text block (a logo->home link is a deliberate future follow-up).
 *
 * v1.19.0 (MO Entry leaves the nav -- Robert, 2026-07-17, assisted-first
 * contract v1.1.0): the manual-entry nav item is REMOVED entirely, and the
 * v1.17/v1.18 muted/disabled machinery (NavItem.muted/disabled/hint, the
 * span-vs-RouterLink conditionals, the navigate() guard) is stripped as dead
 * code -- CTS's business model has no walk-in/radio opportunity class (the
 * Texas Rotation system; every MO enters through the phone system), so no
 * blank-manual-entry affordance should exist. /dispatch survives ONLY as the
 * linked-entry surface: ?draft= promotion (programmatic from AE detail) and
 * the /review ?phone/at/recording deep-link; the router guards blank visits.
 *
 * v1.17.0/v1.18.0 (2026-07-16, superseded same-day pair): grey-out (muted +
 * hint, still clickable) then real disable (muted + inert, all six loops) --
 * the interim training-signal states between full manual entry and removal.
 *
 * v1.16.0 (collapsed-mode drawer -- Robert's refinement, same onboarding):
 * when the desktop sidebar is COLLAPSED, the header hamburger opens the
 * existing mobile slide-over drawer instead of re-pinning the sidebar --
 * pick a page, the drawer auto-closes, the layout never reflows (one-gesture
 * navigation for split-screen work). A desktop-only "Keep menu open" pin row
 * at the top of the drawer re-pins the persistent sidebar. One rule: the
 * menu button always shows the menu; when the menu is pinned, it hides it.
 * The drawer's md:hidden straitjacket lifts only while collapsed, so pinned
 * desktop behavior and the phone drawer are byte-identical to v1.15.0.
 *
 * v1.15.0 (collapsible desktop sidebar -- Robert's ask, Heather split-screen
 * onboarding 2026-07-16): on md+ a header hamburger toggles the sidebar
 * closed -- it slides into the left edge (width 0, clipped, inert) so a
 * split-screen window gives the content pane nearly the full width for data
 * entry. State persists per device (localStorage ops_nav_collapsed); the
 * mobile drawer is untouched (its hamburger stays md:hidden, the desktop
 * toggle is hidden below md). Inner nav content keeps a fixed w-60 so the
 * collapse reads as a slide, not a squish; `inert` removes the collapsed nav
 * from tab order and the a11y tree without hiding it mid-transition. The
 * width transition rides the global reduced-motion kill switch like every
 * other shell transition.
 *
 * v1.14.0 (Module Settings, contract module-settings-api v1.0.0 sect 7):
 * 'Module Settings' nav item (admin group, sliders glyph), gated
 * module_settings exactly like its route.
 *
 * v1.13.0 (error observability, contract error-observability-api v1.0.0
 * SS8): 'Errors' nav item (admin group, alert glyph), gated user_admin
 * exactly like the Audit Log item.
 *
 * v1.12.1 (the ACTUAL RI-chip-missing root cause -- Robert's mobile test +
 * VPS access logs): the badge <span> only existed in the WORKSPACE group's
 * template loops (written in P6 when Assisted-Entry was the only badged
 * item). v1.11.0 taught navBadge() about /review-incentives but that item
 * renders in the MANAGEMENT loop -- count fetched (200s in the access log),
 * stored, and had nowhere to render. Badge span now lives in EVERY group
 * loop, desktop + mobile (renders nothing where navBadge is null), so the
 * class is dead for future badged items. v1.12.0's freshness holes were
 * real but were not this symptom.
 *
 * v1.12.0 (badge-consistency fix, OPERATIONS-S9 -- Robert's report "chips
 * don't consistently show up"): one-shot mount seeding replaced by an
 * eligibility WATCHER + focus/visibilitychange re-seed + 90s visible-only
 * poll, so the AE + RI pending chips appear when counts turn nonzero
 * mid-shift (drafting runs), recover from a failed boot fetch, and survive
 * the forced-change wall. Stage 1 of the async-updates direction.
 *
 * v1.11.0 (RI nav badge, OPERATIONS-S9): the Review Incentives nav item
 * carries the same blue pending-count chip as Assisted-Entry (portal
 * continuity) -- seeded on shell mount for review_incentives_manage holders
 * via reviewIncentives.refreshCounts(); action-live for free (the store
 * self-adjusts counts on attribute/skip/reopen). Badge aria generalized to
 * "pending items" (it no longer only counts drafts).
 * Date: 2026-07-11
 *
 * v1.10.0 (P12): 'System Config' nav item (admin group), gated system_config
 * like its route (contract system-config-api v1.0.0 sect 7, decision #146).
 *
 * v1.9.0 (P8.1, finding #2235): the shell root publishes --app-header-h (the
 * sticky header's live height, ResizeObserver-measured; 61px today) so every
 * descendant sticky can offset below the header instead of sliding under or
 * over it (consumer: reports SectionNav, both variants). Desktop sidebar
 * becomes STATIC (Robert's ask): md:sticky at top var(--app-header-h) with
 * h-[calc(100vh-var)] + self-start, so it holds position while the content
 * pane scrolls with the document. Deliberately NOT a scroll-container change
 * -- the document keeps scrolling and <main> gains no overflow, so every
 * position:sticky inside views (report floater, table headers, AE detail
 * action bar) keeps the document as its scrollport (the v1.3.0 lesson).
 * Mobile drawer untouched.
 *
 * v1.8.0 (P8 polish): Review Incentives gets its own star glyph (backlog
 * #130 -- it shared the generic users icon with User Admin); nav group
 * labels tightened (11px semibold, wider tracking); active nav item gains
 * font-medium; mobile slide-over + user menu get 150-200ms transitions
 * (reduced-motion users get none via the main.css kill switch); user-menu
 * role badges adopt the shared .tag-mini atom. E2E catch: main gains
 * min-w-0 -- its flex min-width:auto default let the /reports/data grid
 * force a page-level h-scrollbar at any viewport narrower than the grid.
 *
 * v1.1.0: 'management' nav group (Review -- decision #122), gated
 * recordings_listen like its route.
 * v1.2.0: active-nav match includes child routes (P5 /reports/standard etc.
 * highlight the Reporting item); shell chrome (header + nav) hidden in print
 * via .no-print (managers print the report views).
 * v1.3.0 (P5.1 batch 2 E2E fix): main overflow-y-auto -> overflow-x-clip.
 * The shell is min-h-screen (it GROWS; the document scrolls), so main's
 * overflow-y-auto never scrolled anything -- but it still made <main> the
 * position:sticky scrollport for every descendant, which silently killed
 * sticky UI inside views (the P5.1 report section nav). overflow-x-clip
 * keeps the horizontal containment safety net WITHOUT creating a scroll
 * container, so descendants stick against the document like the header.
 * v1.4.0 (P6): pending-draft count badge on the Assisted-Entry nav item
 * (refreshes on shell mount for capped users; the assistedQueue store keeps
 * it fresh after every queue/detail/wizard action).
 * v1.5.0 (P9): 'Review Incentives' nav item (management group), gated
 * review_incentives_manage like its routes -- the /review-incentives/reports
 * child highlights the item via the existing isActive child-route match.
 * v1.6.0: 'Help' nav item (decision #142) -- workspace group, NO cap gate
 * (every signed-in role can learn the system; forced-change sessions still
 * see no nav at all).
 * v1.7.0 (P10): 'Call History' nav item (management group), gated
 * call_history_view like its route (contract call-history-api v1.0.0 sect 6).
 *
 * Mobile-FIRST portal shell (doc 12 sect 4.1 tier 1 -- login/nav/shell):
 *   - top bar: brand + user menu (name / change password / sign out) + theme
 *   - nav: hamburger slide-over on mobile (48px tap targets), persistent left
 *     sidebar on md+ (pattern: firstline DefaultLayout, rebuilt cap-gated)
 *   - nav items filter on requiresCap via auth.hasCap (doc 04 sect 9.4);
 *     gating is cosmetic -- the API re-checks caps server-side on every call
 *   - forced-change sessions (mustChange) see NO nav and NO user menu links
 *     other than sign out (doc 04 sect 6.3: block all other nav)
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useSettingsStore } from '@/stores/settings';
import { useAssistedQueueStore } from '@/stores/assistedQueue';
import { useReviewIncentivesStore } from '@/stores/reviewIncentives';
import AppIcon from '@/components/AppIcon.vue';
import type { IconName } from '@/components/AppIcon.vue';

const auth = useAuthStore();
const settings = useSettingsStore();
const assistedQueue = useAssistedQueueStore();
const reviewIncentives = useReviewIncentivesStore();
const route = useRoute();
const router = useRouter();

// Badge freshness (v1.12.0 -- the "chips sometimes don't show" bug). The old
// one-shot onMounted seed had three holes: a count that turns >0 AFTER boot
// (the daytime drafting runs) never surfaced until a tab visit; one failed
// fetch left the badge blank for the whole app boot; and a forced-change
// login mounted the shell with seeding skipped forever. Now: a WATCHER seeds
// the moment eligibility becomes true (auth ready + capped + not walled), a
// focus/visibility re-seed catches second-monitor glances and failed
// fetches, and a 90s visibility-gated poll keeps the counts honest across a
// shift (stage 1 of the async-updates plan). Endpoints 403 harmlessly if a
// cap is revoked mid-session -- the stores swallow and keep the last value.
const canSeedAE = computed(
  () => auth.ready && !auth.mustChange && auth.hasCap('assisted_entry_review'));
const canSeedRI = computed(
  () => auth.ready && !auth.mustChange && auth.hasCap('review_incentives_manage'));

function refreshBadges() {
  if (document.hidden) return; // dual-screen: only refresh what can be seen
  if (canSeedAE.value) void assistedQueue.refreshCount();
  if (canSeedRI.value) void reviewIncentives.refreshCounts();
}

watch([canSeedAE, canSeedRI], refreshBadges, { immediate: true });

let badgeTimer: number | null = null;
onMounted(() => {
  window.addEventListener('focus', refreshBadges);
  document.addEventListener('visibilitychange', refreshBadges);
  badgeTimer = window.setInterval(refreshBadges, 90_000);
});
onBeforeUnmount(() => {
  window.removeEventListener('focus', refreshBadges);
  document.removeEventListener('visibilitychange', refreshBadges);
  if (badgeTimer !== null) window.clearInterval(badgeTimer);
});

// v1.9.0: live header height -> --app-header-h on the shell root. Descendant
// stickies (desktop sidebar here; reports SectionNav rail + dropdown) offset
// below the sticky header with it, staying correct across zoom, font metrics,
// and future header edits. 61px = today's measured height (the pre-mount
// fallback only paints for a frame).
const headerEl = ref<HTMLElement | null>(null);
const headerHeightPx = ref(61);
let headerRo: ResizeObserver | null = null;
onMounted(() => {
  if (headerEl.value) {
    headerHeightPx.value = Math.round(headerEl.value.getBoundingClientRect().height);
    headerRo = new ResizeObserver(() => {
      if (headerEl.value) {
        headerHeightPx.value = Math.round(headerEl.value.getBoundingClientRect().height);
      }
    });
    headerRo.observe(headerEl.value);
  }
});
onBeforeUnmount(() => {
  headerRo?.disconnect();
  headerRo = null;
});

/** Badge count for one nav item (pending-queue chips: Assisted-Entry +
 *  Review Incentives -- v1.11.0 continuity). */
function navBadge(to: string): number | null {
  if (to === '/assisted-entry') {
    const n = assistedQueue.pendingCount;
    return n && n > 0 ? n : null;
  }
  if (to === '/review-incentives') {
    const n = reviewIncentives.counts?.pending ?? null;
    return n && n > 0 ? n : null;
  }
  return null;
}

const mobileNavOpen = ref(false);
const userMenuOpen = ref(false);

// v1.15.0: desktop sidebar collapse -- persisted per device so a
// split-screen dispatcher (Heather's setup) keeps her full-width entry pane
// across sessions. localStorage failures (private mode) degrade to
// session-only state.
const NAV_COLLAPSED_KEY = 'ops_nav_collapsed';
function readNavCollapsed(): boolean {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}
const desktopNavCollapsed = ref(readNavCollapsed());
function persistNavCollapsed() {
  try {
    localStorage.setItem(NAV_COLLAPSED_KEY, desktopNavCollapsed.value ? '1' : '0');
  } catch {
    /* session-only fallback */
  }
}
// v1.16.0: one button, one rule -- "show me the menu". Pinned sidebar ->
// collapse it; collapsed -> open the overlay drawer (auto-closes on
// navigate), so split-screen users navigate in one gesture with zero reflow.
function onDesktopMenuClick() {
  if (desktopNavCollapsed.value) {
    mobileNavOpen.value = true;
  } else {
    desktopNavCollapsed.value = true;
    persistNavCollapsed();
  }
}
// The drawer's pin row: re-pin the persistent sidebar and drop the overlay.
function pinDesktopNav() {
  desktopNavCollapsed.value = false;
  persistNavCollapsed();
  mobileNavOpen.value = false;
}

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  group: 'mo' | 'calls' | 'staff' | 'admin' | 'other';
  requiresCap?: string;
}

// v1.21.0 (decision #176): DOMAIN-grouped nav -- groups say WHAT the pages
// are about (the business domain), not vaguely WHO they're for. Declaration
// order = render order; a group renders only when it has visible items, so
// a pure dispatcher sees just Missed Opportunities + Help.
const NAV_GROUPS: { key: NavItem['group']; label: string | null }[] = [
  { key: 'mo', label: 'Missed Opportunities' },
  { key: 'calls', label: 'Calls' },
  { key: 'staff', label: 'Staff' },
  { key: 'admin', label: 'Administration' },
  { key: 'other', label: null }, // Help -- bottom, no heading
];

// Doc 04 sect 9.4 navItems + the admin sub-surfaces this shell ships.
// /my-entries carries no cap gate (driver read-only landing, always reachable).
// Labels follow decision #176 (machine ids -- routes, caps -- unchanged per A4).
const navItems: NavItem[] = [
  // "Entry Queue" (was Assisted-Entry Review): the queue of system-drafted
  // entries awaiting review -- THE entry path under assisted-first (#173).
  // v1.19.0 note stands: no MO Entry item; /dispatch is reached only via
  // ?draft= promotion and the /review deep-link.
  { to: '/assisted-entry', label: 'Entry Queue', icon: 'robot', group: 'mo', requiresCap: 'assisted_entry_review' },
  { to: '/my-entries', label: 'My Entries', icon: 'list', group: 'mo' },
  // Slice 4 (decision #176 executed): the /review split joins the MO group.
  // Screened Out = calls that didn't become entries + why (the queue half,
  // recordings_listen like the old page); Entry Log = the manager register
  // (the NEW entry_log_view cap, migration 0024 / decision #177).
  { to: '/screened-out', label: 'Screened Out', icon: 'phone-off', group: 'mo', requiresCap: 'recordings_listen' },
  { to: '/entry-log', label: 'Entry Log', icon: 'book', group: 'mo', requiresCap: 'entry_log_view' },
  { to: '/reports', label: 'Reports', icon: 'chart', group: 'mo', requiresCap: 'reporting_view' },
  // P10 Call History (decision #144) -- every ingested call: stats, list,
  // transcripts, downloads. Gated exactly like its route.
  { to: '/call-history', label: 'Call History', icon: 'phone-clock', group: 'calls', requiresCap: 'call_history_view' },
  // Review Incentives (the program's name since the beginning; module is
  // all-staff -- decision #176 keeps the name, the Staff group locates it).
  // Star glyph (backlog #130): its identity is the review star.
  { to: '/review-incentives', label: 'Review Incentives', icon: 'star', group: 'staff', requiresCap: 'review_incentives_manage' },
  // Team Performance (decision #180, contract performance-api v1.0.0):
  // per-user staff metrics -- gated exactly like its route.
  { to: '/performance', label: 'Team Performance', icon: 'bolt', group: 'staff', requiresCap: 'performance_view' },
  { to: '/admin/users', label: 'User Admin', icon: 'users', group: 'admin', requiresCap: 'user_admin' },
  { to: '/admin/pins', label: 'Quick PINs', icon: 'key', group: 'admin', requiresCap: 'user_admin' },
  { to: '/admin/audit', label: 'Audit Log', icon: 'shield', group: 'admin', requiresCap: 'user_admin' },
  // Error observability (contract error-observability-api v1.0.0 SS8) --
  // gated exactly like the Audit Log item.
  { to: '/admin/errors', label: 'Errors', icon: 'alert', group: 'admin', requiresCap: 'user_admin' },
  // P12 System Config (decision #146) -- gated exactly like its route.
  { to: '/admin/system-config', label: 'System Config', icon: 'cog', group: 'admin', requiresCap: 'system_config' },
  // Module Settings (contract module-settings-api v1.0.0) -- per-module
  // behavior knobs; the sibling of System Config. Gated exactly like its route.
  { to: '/admin/module-settings', label: 'Module Settings', icon: 'sliders', group: 'admin', requiresCap: 'module_settings' },
  // Engine Performance (contract performance-api v1.2.0 SS7, decisions #180
  // + #185): the judge-config comparator -- admin-only analytics tier.
  { to: '/admin/engine', label: 'Engine Performance', icon: 'robot', group: 'admin', requiresCap: 'engine_performance_view' },
  // Help (decision #142) -- every signed-in role, no cap gate; bottom seat.
  { to: '/help', label: 'Help', icon: 'help', group: 'other' },
];

const visibleItems = computed(() => {
  if (auth.mustChange) return []; // forced-change wall: no nav at all
  return navItems.filter((i) => !i.requiresCap || auth.hasCap(i.requiresCap));
});
// v1.21.0: ONE grouped structure drives BOTH nav surfaces -- the per-group
// copy-pasted template loops are gone, so a future badged/new item can never
// again exist in some loops and not others (the durable-#20 class, dead by
// construction).
const visibleGroups = computed(() =>
  NAV_GROUPS
    .map((g) => ({ ...g, items: visibleItems.value.filter((i) => i.group === g.key) }))
    .filter((g) => g.items.length > 0));

/** Active when on the item's path OR any of its child routes (/reports/*). */
function isActive(to: string): boolean {
  return route.path === to || route.path.startsWith(`${to}/`);
}

function navigate(to: string) {
  mobileNavOpen.value = false;
  void router.push(to);
}

async function signOut() {
  userMenuOpen.value = false;
  mobileNavOpen.value = false;
  await auth.logout();
  void router.push('/login');
}

function goChangePassword() {
  userMenuOpen.value = false;
  void router.push('/change-password');
}
</script>

<template>
  <div class="min-h-screen flex flex-col bg-bg text-fg" :style="{ '--app-header-h': `${headerHeightPx}px` }">
    <!-- Top bar -->
    <header
      ref="headerEl"
      class="no-print sticky top-0 z-30 flex items-center justify-between gap-2 px-3 md:px-4 py-2 bg-card border-b border-border-c shadow-card"
    >
      <div class="flex items-center gap-2 min-w-0">
        <button
          v-if="visibleItems.length > 0"
          class="p-2.5 -ml-1 rounded-lg hover:bg-input md:hidden"
          aria-label="Open navigation"
          @click="mobileNavOpen = true"
        >
          <AppIcon name="menu" class="w-6 h-6 text-fg-muted" />
        </button>
        <!-- v1.15.0/v1.16.0: desktop menu button -- pinned sidebar collapses;
             collapsed state opens the overlay drawer (one-gesture nav) -->
        <button
          v-if="visibleItems.length > 0"
          class="hidden md:inline-flex p-2.5 -ml-1 rounded-lg hover:bg-input"
          :aria-label="desktopNavCollapsed ? 'Open navigation menu' : 'Collapse navigation'"
          :aria-expanded="!desktopNavCollapsed || mobileNavOpen"
          @click="onDesktopMenuClick"
        >
          <AppIcon name="menu" class="w-6 h-6 text-fg-muted" />
        </button>
        <div class="min-w-0 flex items-center">
          <img
            :src="settings.brandLogo"
            :alt="settings.brandWordmark"
            class="h-7 md:h-8 w-auto max-w-full object-contain object-left select-none"
            draggable="false"
          />
        </div>
      </div>

      <div class="flex items-center gap-1">
        <button
          class="p-2.5 rounded-lg hover:bg-input"
          :aria-label="settings.isDark ? 'Switch to light mode' : 'Switch to dark mode'"
          @click="settings.toggleTheme"
        >
          <AppIcon :name="settings.isDark ? 'sun' : 'moon'" class="w-5 h-5 text-fg-muted" />
        </button>

        <!-- User menu -->
        <div class="relative">
          <button
            class="flex items-center gap-2 pl-2.5 pr-2 py-2 rounded-lg hover:bg-input min-h-[44px]"
            aria-label="User menu"
            @click="userMenuOpen = !userMenuOpen"
          >
            <AppIcon name="user" class="w-5 h-5 text-fg-muted" />
            <span class="hidden sm:inline text-sm text-fg max-w-[10rem] truncate">{{ auth.displayName }}</span>
            <AppIcon name="chevron-down" class="w-4 h-4 text-fg-muted" />
          </button>
          <div
            v-if="userMenuOpen"
            class="fixed inset-0 z-40"
            @click="userMenuOpen = false"
          />
          <Transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="opacity-0 scale-95 -translate-y-1"
            leave-active-class="transition duration-100 ease-in"
            leave-to-class="opacity-0 scale-95 -translate-y-1"
          >
          <div
            v-if="userMenuOpen"
            class="absolute right-0 mt-1 z-50 w-60 bg-card border border-border-c rounded-lg shadow-2xl py-1 origin-top-right"
          >
            <div class="px-4 py-2.5 border-b border-border-c">
              <div class="text-sm font-semibold text-fg truncate">{{ auth.displayName }}</div>
              <div class="text-xs text-fg-muted truncate">{{ auth.user?.email }}</div>
              <div v-if="auth.roles.length" class="mt-1 flex flex-wrap gap-1">
                <span
                  v-for="r in auth.roles"
                  :key="r"
                  class="tag-mini"
                >{{ r }}</span>
              </div>
            </div>
            <button
              v-if="!auth.mustChange"
              class="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-fg hover:bg-input text-left"
              @click="goChangePassword"
            >
              <AppIcon name="lock" class="w-4 h-4 text-fg-muted" /> Change password
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-fg hover:bg-input text-left"
              @click="signOut"
            >
              <AppIcon name="logout" class="w-4 h-4 text-fg-muted" /> Sign out
            </button>
          </div>
          </Transition>
        </div>
      </div>
    </header>

    <div class="flex flex-1 min-h-0">
      <!-- Desktop sidebar: STATIC on md+ (v1.9.0) -- sticky just below the
           header, exactly viewport-tall, its own overflow-y when the list is
           long; the content pane scrolls with the document independently.
           self-start opts out of flex-stretch so sticky has travel. -->
      <nav
        v-if="visibleItems.length > 0"
        class="no-print hidden md:block flex-shrink-0 bg-card md:sticky md:self-start md:top-[var(--app-header-h,61px)] md:h-[calc(100vh-var(--app-header-h,61px))] transition-[width] duration-200 ease-out"
        :class="desktopNavCollapsed
          ? 'w-0 overflow-hidden'
          : 'w-60 border-r border-border-c overflow-y-auto'"
        :inert="desktopNavCollapsed"
      >
        <!-- inner width stays fixed so the collapse slides (clips) instead of
             squishing the labels (v1.15.0) -->
        <!-- v1.21.0: ONE group-driven loop (decision #176 domain groups; the
             per-group copy-paste class -- durable #20 -- is structurally dead) -->
        <div class="py-3 w-60">
          <div v-for="g in visibleGroups" :key="g.key" class="mb-2 last:mb-0">
            <div v-if="g.label" class="px-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-fg-muted">{{ g.label }}</div>
            <RouterLink
              v-for="item in g.items"
              :key="item.to"
              :to="item.to"
              class="flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-input transition-colors"
              :class="isActive(item.to) ? 'bg-input border-l-2 border-accent text-fg font-medium' : 'text-fg-muted border-l-2 border-transparent'"
            >
              <AppIcon :name="item.icon" class="w-5 h-5 flex-shrink-0" />
              <span class="truncate">{{ item.label }}</span>
              <span
                v-if="navBadge(item.to)"
                class="ml-auto text-xs px-1.5 py-0.5 rounded-full bg-accent text-white font-semibold flex-shrink-0"
                :aria-label="`${navBadge(item.to)} pending items`"
              >{{ navBadge(item.to) }}</span>
            </RouterLink>
          </div>
        </div>
      </nav>

      <!-- Slide-over nav drawer: always the phone nav; on md+ it also serves
           the COLLAPSED sidebar (v1.16.0 -- one-gesture navigation without
           re-pinning; md:hidden lifts only while collapsed) -->
      <Transition name="mobilenav">
      <div
        v-if="mobileNavOpen"
        class="no-print fixed inset-0 z-40"
        :class="desktopNavCollapsed ? '' : 'md:hidden'"
      >
        <div class="absolute inset-0 bg-black/60" @click="mobileNavOpen = false" />
        <nav class="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-card border-r border-border-c shadow-2xl flex flex-col">
          <div class="flex items-center justify-between px-4 py-3 border-b border-border-c">
            <img
              :src="settings.brandLogo"
              :alt="settings.brandWordmark"
              class="h-7 w-auto max-w-[200px] object-contain select-none"
              draggable="false"
            />
            <button class="p-2.5 rounded-lg hover:bg-input" aria-label="Close navigation" @click="mobileNavOpen = false">
              <AppIcon name="close" class="w-5 h-5 text-fg-muted" />
            </button>
          </div>
          <!-- v1.16.0: desktop-only pin row -- "back to full width" switch -->
          <button
            v-if="desktopNavCollapsed"
            class="hidden md:flex w-full items-center gap-3 px-4 py-3 text-sm text-left border-b border-border-c text-fg-muted hover:bg-input"
            @click="pinDesktopNav"
          >
            <AppIcon name="menu" class="w-5 h-5 flex-shrink-0" />
            Keep menu open
          </button>
          <!-- v1.21.0: same ONE group-driven loop as the sidebar -->
          <div class="flex-1 overflow-y-auto py-2">
            <div v-for="g in visibleGroups" :key="g.key" class="mb-2 last:mb-0">
              <div v-if="g.label" class="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-fg-muted">{{ g.label }}</div>
              <button
                v-for="item in g.items"
                :key="item.to"
                class="w-full flex items-center gap-3 px-4 py-3.5 text-base text-left min-h-[48px] hover:bg-input"
                :class="isActive(item.to) ? 'bg-input border-l-2 border-accent text-fg font-medium' : 'text-fg-muted border-l-2 border-transparent'"
                @click="navigate(item.to)"
              >
                <AppIcon :name="item.icon" class="w-6 h-6 flex-shrink-0" />
                {{ item.label }}
                <span
                  v-if="navBadge(item.to)"
                  class="ml-auto text-xs px-1.5 py-0.5 rounded-full bg-accent text-white font-semibold flex-shrink-0"
                >{{ navBadge(item.to) }}</span>
              </button>
            </div>
          </div>
          <div class="border-t border-border-c p-3">
            <button class="btn-secondary w-full" @click="signOut">
              <AppIcon name="logout" class="w-5 h-5" /> Sign out
            </button>
          </div>
        </nav>
      </div>
      </Transition>

      <!-- Content. min-w-0: a flex item's min-width:auto default let wide
           children (the /reports/data entry grid's nowrap columns) force
           <main> WIDER than the flex row, inflating document scrollWidth into
           a page-level h-scrollbar; min-w-0 lets main shrink so inner
           overflow-x-auto containers scroll instead (P8 E2E catch). -->
      <main class="flex-1 min-w-0 overflow-x-clip bg-bg">
        <slot />
      </main>
    </div>
  </div>
</template>

<style scoped>
/* Mobile slide-over: overlay fades, drawer slides in from the left.
   Durations stay in the 150-200ms "subtle" band; the global
   prefers-reduced-motion rule in main.css zeroes these out. */
.mobilenav-enter-active,
.mobilenav-leave-active {
  transition: opacity 150ms ease;
}
.mobilenav-enter-active nav,
.mobilenav-leave-active nav {
  transition: transform 200ms ease;
}
.mobilenav-enter-from,
.mobilenav-leave-to {
  opacity: 0;
}
.mobilenav-enter-from nav,
.mobilenav-leave-to nav {
  transform: translateX(-100%);
}
</style>
